'use client'

import React, { useState, useEffect, useMemo } from 'react';
import styles from '../styles/SaftAgreementModal.module.css';
import { Modal } from './Modal'; // Import the generic Modal component

const GAM_TOKEN_PRICE = 0.012; // USD per GAM token
const MINIMUM_USD_AMOUNT = 1000; // Minimum $1000 USD worth of tokens

interface SaftAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  email?: string;
  name?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  inputValue: string;
}

type InputMode = 'usd' | 'gam';

const SaftAgreementModal: React.FC<SaftAgreementModalProps> = ({ isOpen, onClose: propOnClose, email: initialEmail, name: initialName }) => {
  const [formData, setFormData] = useState<FormData>({
    name: initialName || '',
    email: initialEmail || '',
    phone: '',
    inputValue: '',
  });
  const [inputMode, setInputMode] = useState<InputMode>('usd');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string>('');

  useEffect(() => {
    if (initialName) {
      setFormData(prev => ({ ...prev, name: initialName }));
    }
    if (initialEmail) {
      setFormData(prev => ({ ...prev, email: initialEmail }));
    }
    if (!isOpen) { // Reset form aspects when modal is fully closed externally
        setInputMode('usd');
        setSubmitStatus(null);
        setSubmitMessage('');
        setFormData(prev => ({ ...prev, inputValue: ''}));
    }
  }, [initialName, initialEmail, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'inputValue') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const { gamAmount, usdAmount } = useMemo(() => {
    const value = parseFloat(formData.inputValue);
    if (isNaN(value) || value <= 0) {
      return { gamAmount: 0, usdAmount: 0 };
    }
    if (inputMode === 'usd') {
      return {
        usdAmount: value,
        gamAmount: value / GAM_TOKEN_PRICE,
      };
    } else { // inputMode === 'gam'
      return {
        gamAmount: value,
        usdAmount: value * GAM_TOKEN_PRICE,
      };
    }
  }, [formData.inputValue, inputMode]);

  // Validation for minimum amount
  const isAmountValid = useMemo(() => {
    return usdAmount >= MINIMUM_USD_AMOUNT && usdAmount > 0;
  }, [usdAmount]);

  const amountErrorMessage = useMemo(() => {
    if (formData.inputValue && parseFloat(formData.inputValue) > 0 && !isAmountValid) {
      return `Minimum amount required: $${MINIMUM_USD_AMOUNT.toLocaleString()} USD worth of tokens`;
    }
    return null;
  }, [formData.inputValue, isAmountValid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    if (gamAmount <= 0 || usdAmount <= 0) {
        setSubmitStatus('error');
        setSubmitMessage('Please enter a valid amount.');
        setIsSubmitting(false);
        return;
    }

    if (!isAmountValid) {
        setSubmitStatus('error');
        setSubmitMessage(`Minimum SAFT agreement amount is $${MINIMUM_USD_AMOUNT.toLocaleString()} USD worth of tokens.`);
        setIsSubmitting(false);
        return;
    }

    const finalTokenAmountDescription = 
        `${gamAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} GAM (approx. $${usdAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} USD)`;

    try {
      const response = await fetch('/api/saft/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          tokenAmount: finalTokenAmountDescription,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setSubmitMessage(result.message || 'SAFT agreement request submitted successfully. We will be in touch soon.');
        // Keep inputValue for success message context, clear if/when modal truly closes or OK is hit.
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('SAFT submission error:', error);
      setSubmitStatus('error');
      setSubmitMessage('An unexpected error occurred. Please check your connection or try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // This handler is passed to the generic Modal's onClose
  const handleModalClose = () => {
    setSubmitStatus(null); // Reset internal state to show form next time
    setSubmitMessage('');
    setInputMode('usd');
    setFormData(prev => ({ ...prev, inputValue: ''})); // Clear input when modal is closed by X or overlay
    propOnClose(); // Call the original onClose from ClientPurchaseModal
  };

  // This handler is for the "OK" button on the success message
  const handleSuccessOk = () => {
    // Reset state and call the main close prop
    handleModalClose(); 
  };

  // The generic Modal component returns null if !isOpen, so the explicit check is not strictly needed here
  // if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose} // Use the new handler that also resets internal state
      title="SAFT Agreement Inquiry"
    >
      {/* Content for the modal body */}
      <div className={styles.saftModalInternalContentWrapper}> {/* Added wrapper for specific content styling */}
        {submitStatus === 'success' ? (
          <div className={styles.successMessage}>
            <p>{submitMessage}</p>
            <button onClick={handleSuccessOk} className={`${styles.submitButton} ${styles.okButton}`}>OK</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.saftForm}>
            <p className={styles.formDescription}>
              Interested in a SAFT agreement? Please fill out the form below, and our team will contact you shortly to discuss the details.
            </p>
            <div className={styles.formGroup}>
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={styles.inputField}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={styles.inputField}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="phone">Phone Number (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={styles.inputField}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.inputToggleContainer}>
              <button 
                type="button"
                className={`${styles.toggleButton} ${inputMode === 'usd' ? styles.activeToggle : ''}`}
                onClick={() => setInputMode('usd')}
                disabled={isSubmitting}
              >
                Enter USD Amount
              </button>
              <button 
                type="button"
                className={`${styles.toggleButton} ${inputMode === 'gam' ? styles.activeToggle : ''}`}
                onClick={() => setInputMode('gam')}
                disabled={isSubmitting}
              >
                Enter GAM Amount
              </button>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="inputValue">
                {inputMode === 'usd' ? 'Desired Amount in USD' : 'Desired Amount in GAM Tokens'}
              </label>
              <input
                type="text"
                inputMode="decimal"
                id="inputValue"
                name="inputValue"
                value={formData.inputValue}
                onChange={handleChange}
                required
                placeholder="0.00"
                className={styles.inputField}
                disabled={isSubmitting}
              />
            </div>

            {formData.inputValue && parseFloat(formData.inputValue) > 0 && (
              <div className={styles.conversionResult}>
                {inputMode === 'usd' ? (
                  <p>Equivalent to approx: <strong>{gamAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} GAM</strong></p>
                ) : ( 
                  <p>Equivalent to approx: <strong>${usdAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</strong></p>
                )}
              </div>
            )}
            
            {amountErrorMessage && (
              <p className={styles.errorMessage}>{amountErrorMessage}</p>
            )}
            
            {submitStatus === 'error' && (
              <p className={styles.errorMessage}>{submitMessage}</p>
            )}

            <button type="submit" disabled={isSubmitting || gamAmount <=0 || !isAmountValid} className={styles.submitButton}>
              {isSubmitting ? 'Submitting...' : 'Submit SAFT Inquiry'}
            </button>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default SaftAgreementModal; 